<template>
  <div class="calculator-container">
    <b-modal v-model="showWizard" :title="calculatorTitle + ' Setup'" hide-footer size="lg" class="wizard-modal" backdrop-class="wizard-backdrop" @hide="onWizardHide">
      <form-wizard ref="wizard" @on-complete="completeWizard" color="#c7522a" subtitle="Provide operating basics and levers—a worksheet will show Prime Cost, sensitivity, and 5-year impact.">
        <tab-content title="Welcome">
          <div class="wizard-intro">
            <h4 class="mb-4">Participation & Profitability Worksheet</h4>
            <p>This tool helps you factor where to apply leverage in outreach. See how Participation % and Transaction Boost impact Prime Cost and profitability over 1, 3, and 5 years.</p>
            <p class="mb-4">We'll capture operating basics and your key levers. A PDF report is available in the Analysis section.</p>
            <p class="mb-0"><strong>Focus:</strong> Prime Cost optimization and incremental revenue capture—not just savings.</p>
          </div>
        </tab-content>
        <tab-content title="Operating Basics">
          <b-form-group label-for="population" description="Total available population (employees, residents, or eligible diners).">
            <template #label>Total Available Population #</template>
            <b-input id="population" v-model.number="wizardData.population" type="number" min="0" />
          </b-form-group>
          <b-form-group label-for="totalSales" description="Current total sales for the selected period.">
            <template #label>Current Total Sales $</template>
            <currency-input id="totalSales" v-model="wizardData.totalSales" :options="{ currency: 'USD' }" />
          </b-form-group>
          <b-form-group label-for="salesPeriod" :description="`Period for the sales above. Annualized: ${currency(wizardAnnualSales).format()}`">
            <template #label>Sales Period</template>
            <b-form-radio-group id="salesPeriod" v-model="wizardData.salesPeriod" :options="[
              { text: 'Per Day', value: 'daily' },
              { text: 'Per Week', value: 'weekly' },
              { text: 'Per Month', value: 'monthly' },
              { text: 'Annual', value: 'annual' }
            ]" />
          </b-form-group>
          <b-form-group label-for="numTransactions" description="Total transactions for the selected period.">
            <template #label># of Transactions</template>
            <b-input id="numTransactions" v-model.number="wizardData.numTransactions" type="number" min="0" />
          </b-form-group>
          <b-form-group label-for="transactionsPeriod" description="Period for the transaction count.">
            <template #label>Transactions Period</template>
            <b-form-radio-group id="transactionsPeriod" v-model="wizardData.transactionsPeriod" :options="[
              { text: 'Per Day', value: 'daily' },
              { text: 'Per Week', value: 'weekly' },
              { text: 'Per Month', value: 'monthly' }
            ]" />
          </b-form-group>
          <b-form-group label-for="creditCardTransactions" description="Credit card transactions for the selected period.">
            <template #label># Credit Card Transactions</template>
            <b-input id="creditCardTransactions" v-model.number="wizardData.creditCardTransactions" type="number" min="0" />
          </b-form-group>
          <b-form-group label-for="creditCardPeriod" description="Period for credit card transactions.">
            <template #label>Credit Card Period</template>
            <b-form-radio-group id="creditCardPeriod" v-model="wizardData.creditCardPeriod" :options="[
              { text: 'Per Day', value: 'daily' },
              { text: 'Per Week', value: 'weekly' },
              { text: 'Per Month', value: 'monthly' }
            ]" />
          </b-form-group>
          <b-form-group label-for="foodCostPercent" description="Food cost as % of sales. Typical: 28–35%.">
            <template #label>Food Cost %</template>
            <b-input id="foodCostPercent" v-model.number="wizardData.foodCostPercent" type="number" min="0" max="100" />
          </b-form-group>
          <b-form-group label-for="laborCostPercent" description="Labor cost as % of sales. Target: 25–35%. Enter % or $—the other updates automatically.">
            <template #label>Labor Cost %</template>
            <b-input id="laborCostPercent" v-model.number="wizardData.laborCostPercent" type="number" min="0" max="100" />
          </b-form-group>
          <b-form-group label-for="laborCostDollars" description="Labor cost in dollars (annual). Enter % or $—the other updates automatically.">
            <template #label>Labor Cost $ (annual)</template>
            <currency-input id="laborCostDollars" v-model="wizardLaborCostDollarsModel" :options="{ currency: 'USD' }" />
          </b-form-group>
        </tab-content>
        <tab-content title="Levers">
          <b-form-group label-for="participationCurrent" description="Current participation rate (transactions vs. population).">
            <template #label>Current Participation %</template>
            <b-input id="participationCurrent" v-model.number="wizardData.participationCurrent" type="number" min="0" max="100" />
          </b-form-group>
          <b-form-group label-for="participationTarget" description="Target participation rate.">
            <template #label>Target Participation %</template>
            <b-input id="participationTarget" v-model.number="wizardData.participationTarget" type="number" min="0" max="100" />
          </b-form-group>
          <b-form-group label-for="transactionBoost" description="Expected % increase in average check with cashless. Benchmark: 5–10%.">
            <template #label>Transaction Boost (Check Increase) %</template>
            <b-input id="transactionBoost" v-model.number="wizardData.transactionBoost" type="number" min="0" />
          </b-form-group>
          <b-form-group label-for="peakTransactionsPerHour">
            <template #label>Peak Transactions per Hour</template>
            <b-input id="peakTransactionsPerHour" v-model.number="wizardData.peakTransactionsPerHour" type="number" min="0" step="1" />
          </b-form-group>
          <b-form-group label-for="transactionTimeImprovementSeconds">
            <template #label>Seconds Saved per Transaction</template>
            <b-input id="transactionTimeImprovementSeconds" v-model.number="wizardData.transactionTimeImprovementSeconds" type="number" min="0" step="1" />
          </b-form-group>
          <b-form-group label-for="peakHoursPerDay">
            <template #label>Peak Hours per Day</template>
            <b-input id="peakHoursPerDay" v-model.number="wizardData.peakHoursPerDay" type="number" min="0" max="24" step="0.5" />
          </b-form-group>
          <b-form-group label-for="weeklyLaborHours" description="Total staff hours per week (for labor refinement valuation). Optional.">
            <template #label>Weekly Labor Hours (optional)</template>
            <b-input id="weeklyLaborHours" v-model.number="wizardData.weeklyLaborHours" type="number" min="0" />
          </b-form-group>
          <b-form-group label-for="weeklyHoursOpen" description="Hours open per week. Used with seats to calculate RevPASH (Revenue Per Available Seat Hour). Optional.">
            <template #label>Weekly Hours Open (optional)</template>
            <b-input id="weeklyHoursOpen" v-model.number="wizardData.weeklyHoursOpen" type="number" min="0" />
          </b-form-group>
          <b-form-group label-for="totalSeats" description="Total seats. Used with hours open to calculate RevPASH (Revenue Per Available Seat Hour = revenue ÷ seats ÷ hours). Optional.">
            <template #label>Total Seats (optional)</template>
            <b-input id="totalSeats" v-model.number="wizardData.totalSeats" type="number" min="0" />
          </b-form-group>
        </tab-content>
        <template slot="footer" slot-scope="{ activeTabIndex, nextTab, prevTab }">
          <b-button variant="secondary" @click="prevTab" :disabled="activeTabIndex === 0">Previous</b-button>
          <b-button variant="primary" @click="nextTab" v-if="activeTabIndex < 2">Next</b-button>
          <b-button variant="success" @click="completeWizard" v-if="activeTabIndex === 2">Finish</b-button>
        </template>
      </form-wizard>
    </b-modal>

    <div v-if="!showWizard" class="calculator-main">
      <div class="calculator-header">
        <h2 class="calculator-title">{{ calculatorTitle }}</h2>
      </div>
      <b-tabs content-class="calculator-tabs-content" class="main-tabs" v-model="activeTabIndex" @input="onTabChanged">
        <b-tab title="Inputs" active>
          <div class="inputs-container">
            <b-row class="no-gutters">
              <b-col cols="12" lg="7" xl="6">
                <div class="inputs-panel">
                  <div class="input-section">
                    <h4 class="section-title">Operating Basics</h4>
                    <div class="form-field">
                      <label>Total Available Population #</label>
                      <b-input v-model.number="population" type="number" min="0" class="input-field" />
                      <span class="field-description">Employees, residents, or eligible diners.</span>
                    </div>
                    <div class="form-field">
                      <label>Current Total Sales $</label>
                      <currency-input v-model="totalSales" :options="{ currency: 'USD' }" class="input-field" />
                      <b-form-radio-group v-model="salesPeriod" :options="[
                        { text: 'Day', value: 'daily' },
                        { text: 'Week', value: 'weekly' },
                        { text: 'Month', value: 'monthly' },
                        { text: 'Annual', value: 'annual' }
                      ]" class="period-radio" />
                      <span class="field-description">Annualized: {{ currency(annualSales).format() }}</span>
                    </div>
                    <div class="form-field">
                      <label># of Transactions</label>
                      <b-input v-model.number="numTransactions" type="number" min="0" class="input-field" />
                      <b-form-radio-group v-model="transactionsPeriod" :options="[
                        { text: 'Per Day', value: 'daily' },
                        { text: 'Per Week', value: 'weekly' },
                        { text: 'Per Month', value: 'monthly' }
                      ]" class="period-radio" />
                    </div>
                    <div class="form-field">
                      <label># Credit Card Transactions</label>
                      <b-input v-model.number="creditCardTransactions" type="number" min="0" class="input-field" />
                      <b-form-radio-group v-model="creditCardPeriod" :options="[
                        { text: 'Per Day', value: 'daily' },
                        { text: 'Per Week', value: 'weekly' },
                        { text: 'Per Month', value: 'monthly' }
                      ]" class="period-radio" />
                    </div>
                    <div class="form-field">
                      <label>Food Cost %</label>
                      <b-input v-model.number="foodCostPercent" type="number" min="0" max="100" class="input-field" />
                    </div>
                    <div class="form-field">
                      <label>Labor Cost %</label>
                      <b-input v-model.number="laborCostPercent" type="number" min="0" max="100" class="input-field" />
                      <span class="field-description">Enter % or $—the other updates automatically.</span>
                    </div>
                    <div class="form-field">
                      <label>Labor Cost $ (annual)</label>
                      <currency-input v-model="laborCostDollarsModel" :options="{ currency: 'USD' }" class="input-field" />
                    </div>
                  </div>
                  <div class="input-section">
                    <h4 class="section-title">Levers</h4>
                    <div class="form-field">
                      <label>Current Participation %</label>
                      <b-input v-model.number="participationCurrent" type="number" min="0" max="100" class="input-field" />
                      <span class="field-description">Volume lever: current vs. target.</span>
                    </div>
                    <div class="form-field">
                      <label>Target Participation %</label>
                      <b-input v-model.number="participationTarget" type="number" min="0" max="100" class="input-field" />
                    </div>
                    <div class="form-field">
                      <label>Transaction Boost %</label>
                      <b-input v-model.number="transactionBoost" type="number" min="0" class="input-field" />
                      <span class="field-description">Value lever: check size increase. Benchmark 5–10%.</span>
                    </div>
                    <div class="form-field">
                      <label>Peak Transactions per Hour</label>
                      <b-input v-model.number="peakTransactionsPerHour" type="number" min="0" step="1" class="input-field" />
                    </div>
                    <div class="form-field">
                      <label>Seconds Saved per Transaction</label>
                      <b-input v-model.number="transactionTimeImprovementSeconds" type="number" min="0" step="1" class="input-field" />
                    </div>
                    <div class="form-field">
                      <label>Peak Hours per Day</label>
                      <b-input v-model.number="peakHoursPerDay" type="number" min="0" max="24" step="0.5" class="input-field" />
                      <span class="field-description">Hours when you're throughput-limited (e.g. lunch rush).</span>
                    </div>
                    <div class="form-field">
                      <label>Weekly Labor Hours (optional)</label>
                      <b-input v-model.number="weeklyLaborHours" type="number" min="0" class="input-field" />
                      <span class="field-description">For labor refinement valuation.</span>
                    </div>
                    <div class="form-field">
                      <label>Weekly Hours Open (optional)</label>
                      <b-input v-model.number="weeklyHoursOpen" type="number" min="0" class="input-field" />
                      <span class="field-description">Used with seats for RevPASH: revenue ÷ (seats × hours).</span>
                    </div>
                    <div class="form-field">
                      <label>Total Seats (optional)</label>
                      <b-input v-model.number="totalSeats" type="number" min="0" class="input-field" />
                      <span class="field-description">Used with hours open to calculate RevPASH (Revenue Per Available Seat Hour).</span>
                    </div>
                  </div>
                  <div class="actions-bar">
                    <b-button variant="primary" @click="restartWizard">Start Over</b-button>
                  </div>
                </div>
              </b-col>
              <b-col cols="12" lg="5" xl="6">
                <div class="metrics-panel">
                  <h4 class="section-title">Key Metrics</h4>
                  <div class="prime-cost-indicator" :class="primeCostStatusClass">
                    <div class="prime-cost-label">Prime Cost</div>
                    <div class="prime-cost-value">{{ primeCostPercent.toFixed(1) }}%</div>
                    <div class="prime-cost-status">{{ primeCostStatusText }}</div>
                    <div class="prime-cost-note">Target: &lt;60%. Red &gt;65%, Yellow 60–65%</div>
                  </div>
                  <div class="metric-row">
                    <span>Avg Check</span>
                    <span>{{ currency(avgCheck).format() }}</span>
                  </div>
                  <div class="metric-row">
                    <span>Labor Cost $</span>
                    <span>{{ currency(totalLaborCost).format() }}</span>
                  </div>
                  <div class="metric-row">
                    <span>LCPT (Labor Cost per Tx)</span>
                    <span>{{ currency(lcpt).format() }}</span>
                  </div>
                  <div class="metric-row" v-if="revPASH > 0">
                    <span>
                      RevPASH
                      <b-icon icon="question-circle-fill" variant="secondary" class="ml-1" v-b-tooltip.hover="'Revenue Per Available Seat Hour: Total revenue ÷ (seats × hours open). Measures how efficiently you use seating capacity. Higher = better throughput during operating hours. Cashless can raise this by speeding checkout during peak periods.'" />
                    </span>
                    <span>{{ currency(revPASH).format() }}</span>
                  </div>
                  <div class="metric-row metric-note" v-if="revPASH > 0">
                    <span class="revpash-description">Revenue ÷ (Seats × Hours). Efficiency of seating use.</span>
                  </div>
                  <div class="metric-row" v-if="peakTransactionsPerHour > 0 && transactionTimeImprovementSeconds > 0">
                    <span>Peak Throughput</span>
                    <span>{{ peakTransactionsPerHour }} → {{ newPeakTransactionsPerHour.toFixed(0) }}/hr</span>
                  </div>
                  <div class="metric-row metric-note" v-if="peakTransactionsPerHour > 0 && transactionTimeImprovementSeconds > 0">
                    <span class="revpash-description">+{{ peakEfficiencyGain.toFixed(0) }} guests/hr during peak</span>
                  </div>
                  <div class="profit-delta-card">
                    <h5 class="profit-delta-title">5-Year Profit Delta</h5>
                    <p class="profit-delta-explainer">
                      Model output: extra profit over 5 years <em>if</em> you hit Participation {{ participationTarget }}%, +{{ transactionBoost }}% ticket, etc. Big jumps (e.g. 20%→60% participation) = big volume assumptions—verify they're realistic.
                    </p>
                    <div class="profit-delta-baseline">
                      <span>Current sales:</span>
                      <span>{{ currency(annualSales).format() }}/yr</span>
                    </div>
                    <div class="profit-delta-baseline" v-if="annualProfitDelta > 0">
                      <span>
                        Modeled sales (if targets hit)
                        <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 modeled-sales-tip" v-b-tooltip.hover.html="modeledSalesTooltipConfig" />
                      </span>
                      <span>{{ currency(projectedAnnualSales).format() }}/yr</span>
                    </div>
                    <div class="profit-delta-caveat" v-if="annualProfitDelta > 0 && projectedAnnualSales > annualSales * 1.5">
                      <b-icon icon="exclamation-triangle" /> {{ (projectedAnnualSales / annualSales).toFixed(1) }}× current sales—verify participation/target assumptions.
                    </div>
                    <div class="profit-delta-baseline">
                      <span>Current net profit:</span>
                      <span>{{ currency(currentNetProfit).format() }}/yr</span>
                    </div>
                    <div class="profit-delta-baseline" v-if="annualProfitDelta > 0">
                      <span>Modeled net profit (if targets hit):</span>
                      <span>{{ currency(refinedNetProfit).format() }}/yr</span>
                    </div>
                    <div class="profit-delta-annual">
                      <span>Annual delta:</span>
                      <span class="positive">{{ currency(annualProfitDelta).format() }}/yr</span>
                    </div>
                    <div class="profit-delta-breakdown" v-if="annualProfitDelta > 0">
                      <div class="breakdown-label">From:</div>
                      <div class="breakdown-row" v-if="sensitivityScenarios.participation.annual > 0">
                        <span>Participation ({{ participationCurrent }}%→{{ participationTarget }}%)</span>
                        <span>{{ currency(sensitivityScenarios.participation.annual).format() }}/yr</span>
                      </div>
                      <div class="breakdown-row" v-if="sensitivityScenarios.ticketBoost.annual > 0">
                        <span>Transaction Boost (+{{ transactionBoost }}%)</span>
                        <span>{{ currency(sensitivityScenarios.ticketBoost.annual).format() }}/yr</span>
                      </div>
                      <div class="breakdown-row" v-if="sensitivityScenarios.labor.annual > 0">
                        <span>Labor Refinement (peak throughput)</span>
                        <span>{{ currency(sensitivityScenarios.labor.annual).format() }}/yr</span>
                      </div>
                    </div>
                    <div class="profit-delta-breakdown" v-else>
                      <div class="breakdown-label">Increase Participation target, Transaction Boost %, or add Peak tx/hr + Seconds saved to see projected impact.</div>
                    </div>
                    <div class="profit-delta-total">
                      <span>5-year cumulative:</span>
                      <span class="profit-delta-value">{{ currency(fiveYearProfitDelta).format() }}</span>
                    </div>
                    <div class="profit-delta-formula">= {{ currency(annualProfitDelta).format() }} × 5 years</div>
                    <div class="profit-delta-chart">
                      <line-chart :data="metricsPanelChartData" :options="metricsPanelChartOptions" />
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-tab>

        <b-tab title="Analysis">
          <div class="analysis-container">
            <div class="analysis-section executive-summary">
              <h3 class="section-header">Executive Summary</h3>
              <p class="section-disclaimer">
                This worksheet demonstrates how small refinements in Participation, Transaction Value, and Labor Efficiency impact the bottom line.
                Prime Cost optimization and incremental revenue capture—not just savings.
              </p>
              <div class="hero-callout">
                <div class="hero-callout-label">Prime Cost</div>
                <div class="hero-callout-value" :class="primeCostStatusClass">{{ primeCostPercent.toFixed(1) }}%</div>
                <div class="hero-callout-note">{{ primeCostStatusText }} — Target &lt;60%</div>
              </div>
              <b-row>
                <b-col cols="12" md="4">
                  <div class="summary-metric">
                    <div class="metric-label">5-Year Profit Delta</div>
                    <div class="metric-value highlight">{{ currency(fiveYearProfitDelta).format() }}</div>
                    <div class="metric-note">Refined vs. Current</div>
                  </div>
                </b-col>
                <b-col cols="12" md="4">
                  <div class="summary-metric">
                    <div class="metric-label">Peak Throughput Gain</div>
                    <div class="metric-value">{{ peakTransactionsPerHour > 0 && transactionTimeImprovementSeconds > 0 ? '+' + peakEfficiencyGain.toFixed(0) + ' guests/hr' : '—' }}</div>
                    <div class="metric-note">When peak & seconds entered</div>
                  </div>
                </b-col>
                <b-col cols="12" md="4">
                  <div class="summary-metric">
                    <div class="metric-label">LCPT</div>
                    <div class="metric-value">{{ currency(lcpt).format() }}</div>
                    <div class="metric-note">Labor Cost per Transaction</div>
                  </div>
                </b-col>
              </b-row>
            </div>

            <div class="analysis-section">
              <h3 class="section-header">Sensitivity Analysis</h3>
              <p class="section-description">Which lever has the biggest impact on your operation?</p>
              <div class="chart-card">
                <bar-chart ref="sensitivityChart" :data="sensitivityChartData" :options="sensitivityChartOptions" />
              </div>
              <div class="scenario-table-container">
                <table class="scenario-table">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th>Annual Impact</th>
                      <th>5-Year Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Participation ({{ participationCurrent }}% → {{ participationTarget }}%)</strong></td>
                      <td>{{ currency(sensitivityScenarios.participation.annual).format() }}</td>
                      <td>{{ currency(sensitivityScenarios.participation.fiveYear).format() }}</td>
                    </tr>
                    <tr>
                      <td><strong>Transaction Boost (+{{ transactionBoost }}%)</strong></td>
                      <td>{{ currency(sensitivityScenarios.ticketBoost.annual).format() }}</td>
                      <td>{{ currency(sensitivityScenarios.ticketBoost.fiveYear).format() }}</td>
                    </tr>
                    <tr>
                      <td><strong>Labor Refinement</strong> <span v-if="peakTransactionsPerHour > 0 && transactionTimeImprovementSeconds > 0" class="text-muted">({{ peakTransactionsPerHour }}→{{ newPeakTransactionsPerHour.toFixed(0) }}/hr peak)</span></td>
                      <td>{{ currency(sensitivityScenarios.labor.annual).format() }}</td>
                      <td>{{ currency(sensitivityScenarios.labor.fiveYear).format() }}</td>
                    </tr>
                    <tr class="total-row">
                      <td><strong>Combined (Refined Model)</strong></td>
                      <td>{{ currency(sensitivityScenarios.combined.annual).format() }}</td>
                      <td>{{ currency(sensitivityScenarios.combined.fiveYear).format() }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="analysis-section">
              <h3 class="section-header">5-Year Financial Forecast</h3>
              <div class="chart-card">
                <line-chart ref="forecastChart" :data="forecastChartData" :options="forecastChartOptions" />
              </div>
              <div class="forecast-table-container">
                <table class="forecast-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Current Net</th>
                      <th>Refined Net</th>
                      <th>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(y, i) in fiveYearForecast" :key="i">
                      <td>{{ y.year }}</td>
                      <td>{{ currency(y.currentNet).format() }}</td>
                      <td>{{ currency(y.refinedNet).format() }}</td>
                      <td class="positive">{{ currency(y.delta).format() }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="analysis-section">
              <h3 class="section-header">Insights by Environment</h3>
              <b-row>
                <b-col cols="12" md="6">
                  <div class="insight-card">
                    <h4>Healthcare</h4>
                    <p>Safety & Speed. Eliminate "Line Fatigue" so clinical staff can eat during 30-minute breaks. Seconds saved per transaction is a major selling point.</p>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="insight-card">
                    <h4>Corporate</h4>
                    <p>Subsidies & Participation. See how Payroll Deduction increases employees who eat on-site versus leaving for a local drive-thru.</p>
                  </div>
                </b-col>
              </b-row>
              <div class="insight-card full-width">
                <h4>Unbanked Solution</h4>
                <p>Forward-thinking locations implement Reverse-ATMs or Kiosks so staff without traditional bank accounts can participate in the Cashless ecosystem without feeling alienated.</p>
              </div>
            </div>

            <div class="analysis-section">
              <div class="download-pdf-section">
                <h3 class="section-header">Download Your Report</h3>
                <p class="section-description">Get a PDF report of your Participation & Profitability analysis.</p>
                <b-button variant="primary" size="lg" @click="showEmailModal = true" class="download-pdf-btn">
                  <i class="fas fa-download"></i> Download PDF Report
                </b-button>
              </div>
            </div>

            <newcare-cta
              title="Ready to Optimize?"
              :message="`Your 5-year profit delta could be ${currency(fiveYearProfitDelta).format()}. See how Newcare's cashless solution can help.`"
              button-text="Explore Newcare Solutions"
              newcare-url="https://www.newcareinternational.com/cashless-payroll-deduction/"
            />
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <b-modal id="email-capture-modal" v-model="showEmailModal" title="Download Your Report" hide-footer centered size="md">
      <template #default>
        <p class="mb-4">Provide your details to download the PDF report.</p>
        <b-form @submit.prevent="handlePdfDownload">
          <!-- Honeypot: hidden from humans, bots often fill it. If filled, treat as bot. -->
          <div class="honeypot-field" aria-hidden="true">
            <label for="pdf-num-employees">Number of employees</label>
            <input id="pdf-num-employees" v-model="pdfUserData.numEmployeesHoneypot" type="text" name="number_of_employees" tabindex="-1" autocomplete="off" />
          </div>
          <b-form-group label="Company" label-for="pdf-company">
            <b-form-input id="pdf-company" v-model="pdfUserData.company" placeholder="Company or organization" />
          </b-form-group>
          <b-form-group label="Name" label-for="pdf-name">
            <b-form-input id="pdf-name" v-model="pdfUserData.name" required placeholder="Your name" />
          </b-form-group>
          <b-form-group label="Email" label-for="pdf-email">
            <b-form-input id="pdf-email" v-model="pdfUserData.email" type="email" required placeholder="Email address" />
          </b-form-group>
          <div class="d-flex justify-content-end mt-4">
            <b-button variant="secondary" @click="showEmailModal = false" class="mr-2">Cancel</b-button>
            <b-button variant="primary" type="submit" :disabled="!pdfUserData.name || !pdfUserData.email || isGeneratingPdf">
              <span v-if="isGeneratingPdf"><span class="spinner-border spinner-border-sm mr-2"></span>Generating...</span>
              <span v-else>Download PDF</span>
            </b-button>
          </div>
        </b-form>
      </template>
    </b-modal>
  </div>
</template>

<script>
import CurrencyInput from "./CurrencyInput.vue";
import NewcareCTA from "./NewcareCTA.vue";
import Currency from "currency.js";
import { Line, Bar } from "vue-chartjs";
import Chart from "chart.js";

export default {
  name: "ParticipationROI",
  props: {
    calculatorTitle: { type: String, default: "Participation ROI" },
  },
  components: {
    CurrencyInput,
    NewcareCTA,
    "newcare-cta": NewcareCTA,
    LineChart: Line,
    BarChart: Bar,
  },
  data() {
    return {
      showWizard: true,
      wizardData: {
        population: 500,
        totalSales: 5000,
        salesPeriod: "weekly",
        numTransactions: 200,
        transactionsPeriod: "weekly",
        creditCardTransactions: 100,
        creditCardPeriod: "weekly",
        foodCostPercent: 30,
        laborCostPercent: 35,
        participationCurrent: 20,
        participationTarget: 60,
        transactionBoost: 8,
        peakTransactionsPerHour: 0,
        transactionTimeImprovementSeconds: 0,
        peakHoursPerDay: 2,
        weeklyLaborHours: 98,
        totalSeats: 0,
        weeklyHoursOpen: 98,
      },
      activeTabIndex: 0,
      population: 500,
      totalSales: 5000,
      salesPeriod: "weekly",
      numTransactions: 200,
      transactionsPeriod: "weekly",
      creditCardTransactions: 100,
      creditCardPeriod: "weekly",
      foodCostPercent: 30,
      laborCostPercent: 35,
      participationCurrent: 20,
      participationTarget: 60,
      transactionBoost: 8,
      peakTransactionsPerHour: 0,
      transactionTimeImprovementSeconds: 0,
      peakHoursPerDay: 2,
      weeklyLaborHours: 98,
      totalSeats: 0,
      weeklyHoursOpen: 98,
      showEmailModal: false,
      pdfUserData: { company: "", name: "", email: "", numEmployeesHoneypot: "" },
      isGeneratingPdf: false,
    };
  },
  computed: {
    salesMultiplier() {
      const m = { daily: 365, weekly: 52, monthly: 12, annual: 1 };
      return m[this.salesPeriod] || 1;
    },
    transactionsMultiplier() {
      const m = { daily: 365, weekly: 52, monthly: 12 };
      return m[this.transactionsPeriod] || 1;
    },
    creditCardMultiplier() {
      const m = { daily: 365, weekly: 52, monthly: 12 };
      return m[this.creditCardPeriod] || 1;
    },
    annualSales() {
      return (this.totalSales || 0) * this.salesMultiplier;
    },
    wizardAnnualSales() {
      const s = this.wizardData.totalSales || 0;
      const m = { daily: 365, weekly: 52, monthly: 12, annual: 1 }[this.wizardData.salesPeriod] || 1;
      return s * m;
    },
    wizardLaborCostDollars() {
      return this.wizardAnnualSales * (this.wizardData.laborCostPercent / 100) || 0;
    },
    laborCostDollarsModel: {
      get() {
        return this.totalLaborCost;
      },
      set(val) {
        const parsed = typeof val === "number" ? val : parseFloat(String(val).replace(/[^0-9.-]/g, "")) || 0;
        if (this.annualSales > 0) {
          const pct = (parsed / this.annualSales) * 100;
          this.laborCostPercent = Math.round(Math.min(100, Math.max(0, pct)) * 1000) / 1000;
        }
      },
    },
    wizardLaborCostDollarsModel: {
      get() {
        return this.wizardLaborCostDollars;
      },
      set(val) {
        const parsed = typeof val === "number" ? val : parseFloat(String(val).replace(/[^0-9.-]/g, "")) || 0;
        if (this.wizardAnnualSales > 0) {
          const pct = (parsed / this.wizardAnnualSales) * 100;
          this.wizardData.laborCostPercent = Math.round(Math.min(100, Math.max(0, pct)) * 1000) / 1000;
        }
      },
    },
    annualTransactions() {
      return (this.numTransactions || 0) * this.transactionsMultiplier;
    },
    annualCreditCardTransactions() {
      return (this.creditCardTransactions || 0) * this.creditCardMultiplier;
    },
    avgCheck() {
      return this.annualTransactions > 0 ? this.annualSales / this.annualTransactions : 0;
    },
    totalFoodCost() {
      return this.annualSales * (this.foodCostPercent / 100) || 0;
    },
    totalLaborCost() {
      return this.annualSales * (this.laborCostPercent / 100) || 0;
    },
    primeCostPercent() {
      return (this.foodCostPercent || 0) + (this.laborCostPercent || 0);
    },
    primeCostStatusClass() {
      const p = this.primeCostPercent;
      if (p > 65) return "status-red";
      if (p >= 60) return "status-yellow";
      return "status-green";
    },
    primeCostStatusText() {
      const p = this.primeCostPercent;
      if (p > 65) return "Review";
      if (p >= 60) return "Borderline";
      return "Healthy";
    },
    lcpt() {
      return this.annualTransactions > 0 ? this.totalLaborCost / this.annualTransactions : 0;
    },
    annualHoursOpen() {
      return (this.weeklyHoursOpen || 0) * 52;
    },
    revPASH() {
      const seats = this.totalSeats || 0;
      const hours = this.annualHoursOpen || 0;
      if (seats <= 0 || hours <= 0) return 0;
      return this.annualSales / (seats * hours);
    },
    peakEfficiencyGain() {
      const P = this.peakTransactionsPerHour || 0;
      const S = this.transactionTimeImprovementSeconds || 0;
      if (P <= 0 || S <= 0) return 0;
      const secPerTx = 3600 / P;
      if (S >= secPerTx) return 0;
      const newSecPerTx = secPerTx - S;
      const newPeak = 3600 / newSecPerTx;
      return Math.max(0, newPeak - P);
    },
    newPeakTransactionsPerHour() {
      const P = this.peakTransactionsPerHour || 0;
      const S = this.transactionTimeImprovementSeconds || 0;
      if (P <= 0 || S <= 0) return P;
      const secPerTx = 3600 / P;
      if (S >= secPerTx) return P;
      return 3600 / (secPerTx - S);
    },
    laborCostPerHour() {
      const wh = this.weeklyLaborHours || 0;
      if (wh <= 0) return this.totalLaborCost / 2000;
      return this.totalLaborCost / (wh * 52);
    },
    laborRefinementSavingsAnnual() {
      const gain = this.peakEfficiencyGain;
      const H = this.peakHoursPerDay || 0;
      if (gain <= 0 || H <= 0) return 0;
      const additionalTxPerYear = gain * H * 365;
      const additionalRevenue = additionalTxPerYear * this.avgCheck;
      return additionalRevenue * (1 - this.foodCostPercent / 100);
    },
    currentNetProfit() {
      const ccFees = this.annualCreditCardTransactions * (this.avgCheck * 0.029 + 0.3);
      return (
        this.annualSales -
        this.totalFoodCost -
        this.totalLaborCost -
        ccFees
      );
    },
    // Net profit margin: revenue minus food cost and labor cost (Prime Cost)
    netProfitMargin() {
      return Math.max(0, 1 - this.foodCostPercent / 100 - this.laborCostPercent / 100);
    },
    participationRevenueIncrease() {
      if (this.participationTarget <= this.participationCurrent) return 0;
      const cur = Math.max(this.participationCurrent, 1);
      const additionalTx = this.annualTransactions * (this.participationTarget - this.participationCurrent) / cur;
      const additionalRevenue = additionalTx * this.avgCheck;
      return additionalRevenue * this.netProfitMargin;
    },
    ticketBoostRevenueIncrease() {
      const boost = (this.transactionBoost || 0) / 100;
      const extraRevenue = this.annualSales * boost;
      return extraRevenue * this.netProfitMargin;
    },
    sensitivityScenarios() {
      const part = this.participationRevenueIncrease;
      const ticket = this.ticketBoostRevenueIncrease;
      const labor = this.laborRefinementSavingsAnnual;
      const combined = part + ticket + labor;
      return {
        participation: { annual: part, fiveYear: part * 5 },
        ticketBoost: { annual: ticket, fiveYear: ticket * 5 },
        labor: { annual: labor, fiveYear: labor * 5 },
        combined: { annual: combined, fiveYear: combined * 5 },
      };
    },
    // Additional revenue (gross) from each lever, before food/labor cost
    participationAdditionalRevenue() {
      if (this.participationTarget <= this.participationCurrent) return 0;
      const cur = Math.max(this.participationCurrent, 1);
      const additionalTx = this.annualTransactions * (this.participationTarget - this.participationCurrent) / cur;
      return additionalTx * this.avgCheck;
    },
    ticketBoostAdditionalRevenue() {
      const boost = (this.transactionBoost || 0) / 100;
      return this.annualSales * boost;
    },
    laborRefinementAdditionalRevenue() {
      const gain = this.peakEfficiencyGain;
      const H = this.peakHoursPerDay || 0;
      if (gain <= 0 || H <= 0) return 0;
      const additionalTxPerYear = gain * H * 365;
      return additionalTxPerYear * this.avgCheck;
    },
    projectedAnnualSales() {
      return (
        this.annualSales +
        this.participationAdditionalRevenue +
        this.ticketBoostAdditionalRevenue +
        this.laborRefinementAdditionalRevenue
      );
    },
    /**
     * Tooltip: How modeled sales is calculated.
     *
     * Modeled sales = Current sales + Participation gain + Transaction boost + Labor refinement
     *
     * 1. Participation (X%→Y%): Assumes (Y/X)× transaction volume. Formula: additionalTx = annualTransactions × (Y-X)/X.
     *    Additional revenue = additionalTx × avgCheck. E.g. 20%→60% = 3× volume = +2× current transactions.
     *
     * 2. Transaction Boost (+Z%): Adds Z% of current sales. E.g. +8% = 0.08 × annualSales.
     *
     * 3. Labor Refinement: If peak tx/hr and seconds saved entered, adds revenue from extra guests served during peak.
     *
     * Big participation jumps (e.g. 20%→60%) drive large volume assumptions—verify they're realistic.
     */
    modeledSalesTooltip() {
      const parts = [];
      parts.push('Modeled sales = Current + Participation gain + Transaction boost + Labor refinement');
      parts.push('');
      parts.push(`Current: ${this.currency(this.annualSales).format()}`);
      if (this.participationAdditionalRevenue > 0) {
        const addlTx = Math.round(this.annualTransactions * (this.participationTarget - this.participationCurrent) / Math.max(this.participationCurrent, 1));
        parts.push(`+ Participation (${this.participationCurrent}%→${this.participationTarget}%): ${this.currency(this.participationAdditionalRevenue).format()} (${addlTx.toLocaleString()} more tx × ${this.currency(this.avgCheck).format()} avg check)`);
      }
      if (this.ticketBoostAdditionalRevenue > 0) {
        parts.push(`+ Transaction Boost (+${this.transactionBoost}%): ${this.currency(this.ticketBoostAdditionalRevenue).format()} (${this.transactionBoost}% of current sales)`);
      }
      if (this.laborRefinementAdditionalRevenue > 0) {
        parts.push(`+ Labor Refinement: ${this.currency(this.laborRefinementAdditionalRevenue).format()} (peak throughput)`);
      }
      parts.push(`= ${this.currency(this.projectedAnnualSales).format()}`);
      parts.push('');
      parts.push(`Participation ${this.participationCurrent}%→${this.participationTarget}% assumes ${(this.participationTarget / this.participationCurrent).toFixed(1)}× transaction volume. Verify this is realistic.`);
      return parts.join('<br>');
    },
    modeledSalesTooltipConfig() {
      return {
        title: this.modeledSalesTooltip,
        html: true,
        customClass: 'modeled-sales-tooltip',
      };
    },
    refinedNetProfit() {
      return (
        this.currentNetProfit +
        this.sensitivityScenarios.combined.annual
      );
    },
    annualProfitDelta() {
      return this.sensitivityScenarios.combined.annual;
    },
    fiveYearProfitDelta() {
      return this.sensitivityScenarios.combined.fiveYear;
    },
    metricsPanelChartData() {
      const f = this.fiveYearForecast;
      return {
        labels: f.map((x) => x.year),
        datasets: [
          {
            label: "Current Net Profit",
            data: f.map((x) => x.currentNet),
            borderColor: "#74a892",
            backgroundColor: "rgba(74,168,146,0.15)",
            borderWidth: 2,
            fill: true,
          },
          {
            label: "Projected Net Profit",
            data: f.map((x) => x.refinedNet),
            borderColor: "#c7522a",
            backgroundColor: "rgba(199,82,42,0.15)",
            borderWidth: 2,
            fill: true,
          },
        ],
      };
    },
    metricsPanelChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { labels: { fontColor: "#fbf2c4", boxWidth: 12 } },
        scales: {
          yAxes: [
            {
              ticks: { beginAtZero: true, fontColor: "#fbf2c4", fontSize: 10 },
              gridLines: { color: "rgba(251,242,196,0.1)" },
            },
          ],
          xAxes: [{ ticks: { fontColor: "#fbf2c4", fontSize: 10 }, gridLines: { color: "rgba(251,242,196,0.1)" } }],
        },
        tooltips: { backgroundColor: "#2a344a" },
      };
    },
    fiveYearForecast() {
      const arr = [];
      let curCum = 0;
      let refCum = 0;
      for (let y = 1; y <= 5; y++) {
        curCum += this.currentNetProfit;
        refCum += this.refinedNetProfit;
        arr.push({
          year: `Year ${y}`,
          currentNet: this.currentNetProfit,
          refinedNet: this.refinedNetProfit,
          delta: this.refinedNetProfit - this.currentNetProfit,
        });
      }
      return arr;
    },
    sensitivityChartData() {
      const s = this.sensitivityScenarios;
      return {
        labels: ["Participation", "Transaction Boost", "Labor Refinement", "Combined"],
        datasets: [
          {
            label: "Annual Net Impact",
            data: [s.participation.annual, s.ticketBoost.annual, s.labor.annual, s.combined.annual],
            backgroundColor: [
              "rgba(199, 82, 42, 0.6)",
              "rgba(74, 168, 146, 0.6)",
              "rgba(251, 242, 196, 0.6)",
              "rgba(200, 100, 200, 0.6)",
            ],
            borderColor: ["#c7522a", "#74a892", "#fbf2c4", "#c864c8"],
            borderWidth: 1,
          },
        ],
      };
    },
    sensitivityChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, fontColor: "#fbf2c4" }, gridLines: { color: "rgba(251,242,196,0.1)" } }],
          xAxes: [{ ticks: { fontColor: "#fbf2c4" }, gridLines: { color: "rgba(251,242,196,0.1)" } }],
        },
        legend: { labels: { fontColor: "#fbf2c4" } },
        tooltips: { backgroundColor: "#2a344a" },
      };
    },
    forecastChartData() {
      const f = this.fiveYearForecast;
      return {
        labels: f.map((x) => x.year),
        datasets: [
          {
            label: "Current Net",
            data: f.map((x) => x.currentNet),
            borderColor: "#74a892",
            backgroundColor: "rgba(74,168,146,0.2)",
            borderWidth: 2,
          },
          {
            label: "Refined Net",
            data: f.map((x) => x.refinedNet),
            borderColor: "#c7522a",
            backgroundColor: "rgba(199,82,42,0.2)",
            borderWidth: 2,
          },
        ],
      };
    },
    forecastChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, fontColor: "#fbf2c4" }, gridLines: { color: "rgba(251,242,196,0.1)" } }],
          xAxes: [{ ticks: { fontColor: "#fbf2c4" }, gridLines: { color: "rgba(251,242,196,0.1)" } }],
        },
        legend: { labels: { fontColor: "#fbf2c4" } },
        tooltips: { backgroundColor: "#2a344a" },
      };
    },
  },
  methods: {
    currency(val) {
      return Currency(val || 0, { precision: 2, symbol: "$" });
    },
    completeWizard() {
      this.population = this.wizardData.population;
      this.totalSales = this.wizardData.totalSales;
      this.salesPeriod = this.wizardData.salesPeriod;
      this.numTransactions = this.wizardData.numTransactions;
      this.transactionsPeriod = this.wizardData.transactionsPeriod;
      this.creditCardTransactions = this.wizardData.creditCardTransactions;
      this.creditCardPeriod = this.wizardData.creditCardPeriod;
      this.foodCostPercent = this.wizardData.foodCostPercent;
      this.laborCostPercent = this.wizardData.laborCostPercent;
      this.participationCurrent = this.wizardData.participationCurrent;
      this.participationTarget = this.wizardData.participationTarget;
      this.transactionBoost = this.wizardData.transactionBoost;
      this.peakTransactionsPerHour = this.wizardData.peakTransactionsPerHour;
      this.transactionTimeImprovementSeconds = this.wizardData.transactionTimeImprovementSeconds;
      this.peakHoursPerDay = this.wizardData.peakHoursPerDay;
      this.weeklyLaborHours = this.wizardData.weeklyLaborHours;
      this.totalSeats = this.wizardData.totalSeats;
      this.weeklyHoursOpen = this.wizardData.weeklyHoursOpen;
      this.showWizard = false;
    },
    onWizardHide() {
      if (!this.showWizard) return;
      this.completeWizard();
    },
    restartWizard() {
      this.showWizard = true;
    },
    onTabChanged() {
      this.$nextTick(() => {
        if (this.$refs.sensitivityChart) this.$refs.sensitivityChart.renderChart(this.sensitivityChartData, this.sensitivityChartOptions);
        if (this.$refs.forecastChart) this.$refs.forecastChart.renderChart(this.forecastChartData, this.forecastChartOptions);
      });
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
    },
    async handlePdfDownload() {
      if (!this.pdfUserData.name || !this.pdfUserData.email || !this.isValidEmail(this.pdfUserData.email)) return;
      this.isGeneratingPdf = true;
      try {
        await this.sendEmailCapture();
        await this.generatePdf();
      } finally {
        this.isGeneratingPdf = false;
      }
    },
    async sendEmailCapture() {
      try {
        const formData = new URLSearchParams();
        formData.append("form-name", "pdf-download");
        formData.append("name", (this.pdfUserData.name || "").trim());
        formData.append("email", (this.pdfUserData.email || "").trim());
        formData.append("company", (this.pdfUserData.company || "").trim());
        formData.append("calculator", "participation");
        formData.append("calculatorData", JSON.stringify(this.getCalculatorSummary()));
        formData.append("bot-field", this.pdfUserData.numEmployeesHoneypot || "");

        await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString()
        });
      } catch (error) {
        // Don't throw - allow PDF download even if email capture fails
      }
    },
    getCalculatorSummary() {
      return {
        population: this.population,
        totalSales: this.totalSales,
        salesPeriod: this.salesPeriod,
        numTransactions: this.numTransactions,
        transactionsPeriod: this.transactionsPeriod,
        creditCardTransactions: this.creditCardTransactions,
        creditCardPeriod: this.creditCardPeriod,
        foodCostPercent: this.foodCostPercent,
        laborCostPercent: this.laborCostPercent,
        participationCurrent: this.participationCurrent,
        participationTarget: this.participationTarget,
        transactionBoost: this.transactionBoost,
        peakTransactionsPerHour: this.peakTransactionsPerHour,
        transactionTimeImprovementSeconds: this.transactionTimeImprovementSeconds,
        peakHoursPerDay: this.peakHoursPerDay,
        weeklyLaborHours: this.weeklyLaborHours,
        totalSeats: this.totalSeats,
        weeklyHoursOpen: this.weeklyHoursOpen
      };
    },
    async generatePdf() {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;
      const primaryColor = [199, 82, 42];
      const dark = [40, 40, 40];
      const gray = [90, 90, 90];

      this.$nextTick();
      await new Promise((r) => setTimeout(r, 400));
      const chartImg = this.$refs.sensitivityChart ? this.$refs.sensitivityChart.$el.toDataURL("image/png") : null;

      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 50, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont(undefined, "bold");
      doc.text("Participation & Profitability", pageWidth / 2, 22, { align: "center" });
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text("Prime Cost Optimization • C-Suite ROI Worksheet", pageWidth / 2, 35, { align: "center" });
      doc.text(`Generated: ${new Date().toLocaleDateString("en-US")}`, pageWidth / 2, 44, { align: "center" });

      let y = 65;
      doc.setTextColor(...dark);
      const company = (this.pdfUserData.company || "").trim();
      const name = (this.pdfUserData.name || "").trim();
      if (company) {
        doc.setFontSize(11);
        doc.text(`Prepared for: ${company}`, margin, y);
        y += 8;
      }
      if (name) {
        doc.text(name, margin, y);
        y += 12;
      }

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Executive Summary", margin, y);
      y += 10;
      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      doc.text(`Prime Cost: ${this.primeCostPercent.toFixed(1)}% (${this.primeCostStatusText})`, margin, y);
      y += 6;
      doc.text(`5-Year Profit Delta: ${this.currency(this.fiveYearProfitDelta).format()}`, margin, y);
      y += 6;
      const peakGain = this.peakTransactionsPerHour > 0 && this.transactionTimeImprovementSeconds > 0
        ? `Peak Throughput: +${this.peakEfficiencyGain.toFixed(0)} guests/hr`
        : 'Peak Throughput: — (enter peak tx/hr & seconds)';
      doc.text(peakGain, margin, y);
      y += 6;
      doc.text(`LCPT: ${this.currency(this.lcpt).format()}`, margin, y);
      y += 12;

      doc.setFont(undefined, "bold");
      doc.text("Sensitivity Analysis", margin, y);
      y += 8;
      doc.setFont(undefined, "normal");
      const s = this.sensitivityScenarios;
      doc.text(`Participation: ${this.currency(s.participation.annual).format()}/yr`, margin, y);
      y += 6;
      doc.text(`Transaction Boost: ${this.currency(s.ticketBoost.annual).format()}/yr`, margin, y);
      y += 6;
      doc.text(`Labor Refinement: ${this.currency(s.labor.annual).format()}/yr`, margin, y);
      y += 6;
      doc.text(`Combined: ${this.currency(s.combined.annual).format()}/yr`, margin, y);
      y += 12;

      if (chartImg && chartImg.length > 100) {
        doc.addImage(chartImg, "PNG", margin, y, contentWidth, 50);
        y += 55;
      }

      doc.setFont(undefined, "bold");
      doc.text("Insights", margin, y);
      y += 8;
      doc.setFont(undefined, "normal");
      doc.text("Healthcare: Safety & Speed—eliminate Line Fatigue. Corporate: Subsidies & Participation.", margin, y, { maxWidth: contentWidth });
      y += 8;
      doc.text("Unbanked: Reverse-ATMs or Kiosks enable participation without alienation.", margin, y, { maxWidth: contentWidth });
      y += 15;

      doc.setFontSize(8);
      doc.setTextColor(...gray);
      doc.text("www.newcare.io • cashlessFyi@newcare.io • cashless.fyi", pageWidth / 2, pageHeight - 10, { align: "center" });

      doc.save(`Participation-ROI-${new Date().toISOString().slice(0, 10)}.pdf`);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/app.scss";

.calculator-container {
  width: 100%;
  max-width: 100%;
  padding: 0;
  min-height: calc(100vh - 120px);
}

.calculator-header {
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid rgba($body-color, 0.1);
  background: lighten($body-bg, 3%);
  flex-shrink: 0;

  .calculator-title {
    font-size: 2rem;
    font-weight: 700;
    color: $body-color;
    margin: 0;
  }
}

.calculator-main {
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

// Main tabs - content expands; single page scroll only (no column scrollbars)
.main-tabs {
  display: flex;
  flex-direction: column;

  ::v-deep .nav-tabs {
    border-bottom: 2px solid rgba($body-color, 0.2);
    margin-bottom: 0;
    padding: 0 2rem;
    background: lighten($body-bg, 3%);
    flex-shrink: 0;

    .nav-item {
      .nav-link {
        color: rgba($body-color, 0.7);
        background: transparent;
        border: none;
        border-bottom: 3px solid transparent;
        padding: 1rem 2rem;
        font-size: 1.1rem;
        font-weight: 600;
        transition: all 0.2s ease;

        &:hover {
          color: $body-color;
          border-bottom-color: rgba($primary, 0.5);
        }

        &.active {
          color: $primary;
          background: transparent;
          border-bottom-color: $primary;
        }
      }
    }
  }

  ::v-deep .tab-content.calculator-tabs-content {
    padding: 0;
  }
}

.inputs-container {
  width: 100%;
  padding: 0;

  .row {
    align-items: stretch;
  }
}

.inputs-panel {
  padding: 1.5rem 2rem;
  background: lighten($body-bg, 2%);
  min-height: 400px;

  .input-section {
    background: lighten($body-bg, 5%);
    border: 1px solid rgba($body-color, 0.1);
    border-radius: 0.375rem;
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    .section-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: $primary;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid rgba($primary, 0.2);
    }
  }
  .form-field {
    margin-bottom: 1rem;
    label {
      display: block;
      font-weight: 600;
      margin-bottom: 0.35rem;
      color: rgba($body-color, 0.9);
    }
    .input-field {
      width: 100%;
      max-width: 220px;
      background: lighten($body-bg, 10%) !important;
      color: $body-color !important;
      border: 1px solid rgba($body-color, 0.25) !important;
      border-radius: 0.25rem;
    }
    .field-description {
      display: block;
      font-size: 0.85rem;
      color: rgba($body-color, 0.7);
      margin-top: 0.25rem;
    }
  }
  .actions-bar {
    margin-top: 1.5rem;
  }
}

.metrics-panel {
  padding: 1.5rem 2rem;
  background: lighten($body-bg, 4%);
  border-left: 1px solid rgba($body-color, 0.1);
  min-height: 400px;

  .section-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
  }
}

.analysis-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.analysis-section {
  margin-bottom: 2.5rem;
  .section-header {
    font-size: 1.35rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
  }
  .section-description {
    color: rgba($body-color, 0.85);
    margin-bottom: 1rem;
    font-size: 0.95rem;
  }
}

.executive-summary {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($primary, 0.2);
  border-radius: 0.5rem;
  padding: 2rem;

  .section-disclaimer {
    color: rgba($body-color, 0.85);
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba($body-color, 0.05);
    border-radius: 0.375rem;
    border-left: 4px solid rgba($primary, 0.2);
  }

  .summary-metric {
    text-align: center;
    padding: 1.5rem;
    background: lighten($body-bg, 3%);
    border-radius: 0.375rem;
    border: 1px solid rgba($body-color, 0.1);

    .metric-label {
      font-size: 0.95rem;
      font-weight: 600;
      color: rgba($body-color, 0.8);
      margin-bottom: 0.5rem;
    }

    .metric-value {
      font-size: 1.1rem;
      font-weight: 700;
      color: $body-color;
      margin-bottom: 0.25rem;

      &.highlight {
        color: $primary;
        font-size: 1.5rem;
      }
    }

    .metric-note {
      font-size: 0.75rem;
      color: rgba($body-color, 0.6);
    }
  }
}

.hero-callout {
  background: rgba($primary, 0.4);
  border: 1px solid $primary;
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  .hero-callout-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.9);
  }
  .hero-callout-value {
    font-size: 2rem;
    font-weight: 700;
    color: white;
    &.status-green { color: $secondary; }
    &.status-yellow { color: darken($body-color, 15%); }
    &.status-red { color: lighten($primary, 15%); }
  }
  .hero-callout-note {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 0.25rem;
  }
}

.chart-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  min-height: 280px;
}

.period-radio {
  margin-top: 0.5rem;
  font-size: 0.9rem;
}

.prime-cost-indicator {
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  text-align: center;
  &.status-green {
    background: rgba($secondary, 0.3);
    border: 2px solid $secondary;
  }
  &.status-yellow {
    background: rgba($body-color, 0.15);
    border: 2px solid darken($body-color, 10%);
  }
  &.status-red {
    background: rgba($primary, 0.3);
    border: 2px solid $primary;
  }
  .prime-cost-value {
    font-size: 1.75rem;
    font-weight: 700;
  }
}

.metric-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba($body-color, 0.2);

  &.metric-note {
    border-bottom: none;
    padding-top: 0;
  }
  .revpash-description {
    font-size: 0.8rem;
    color: rgba($body-color, 0.65);
    font-style: italic;
  }
}

.profit-delta-card {
  margin-top: 1.5rem;
  padding: 1rem;
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($primary, 0.3);
  border-radius: 0.5rem;
  font-size: 0.9rem;

  .profit-delta-title {
    font-size: 1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 0.5rem;
  }

  .profit-delta-explainer {
    font-size: 0.8rem;
    color: rgba($body-color, 0.8);
    margin-bottom: 0.75rem;
    line-height: 1.4;
  }

  .profit-delta-caveat {
    font-size: 0.75rem;
    color: rgba($primary, 0.95);
    margin-top: 0.25rem;
    margin-bottom: 0.5rem;
    padding: 0.35rem 0.5rem;
    background: rgba($primary, 0.15);
    border-radius: 0.25rem;
  }

  .profit-delta-baseline,
  .profit-delta-annual,
  .profit-delta-total {
    display: flex;
    justify-content: space-between;
    padding: 0.25rem 0;
    border-bottom: 1px solid rgba($body-color, 0.15);
  }

  .profit-delta-annual {
    font-weight: 600;
  }

  .profit-delta-breakdown {
    margin-top: 0.5rem;
    padding: 0.5rem;
    background: rgba($body-color, 0.05);
    border-radius: 0.25rem;

    .breakdown-label {
      font-size: 0.75rem;
      color: rgba($body-color, 0.7);
      margin-bottom: 0.25rem;
    }

    .breakdown-row {
      display: flex;
      justify-content: space-between;
      font-size: 0.8rem;
      padding: 0.15rem 0;
    }
  }

  .profit-delta-total {
    margin-top: 0.5rem;
    font-weight: 700;
    border-bottom: none;
  }

  .profit-delta-value {
    color: $secondary;
    font-size: 1.1rem;
  }

  .profit-delta-formula {
    font-size: 0.75rem;
    color: rgba($body-color, 0.6);
    margin-top: 0.25rem;
  }

  .profit-delta-chart {
    margin-top: 0.75rem;
    height: 140px;
  }
}

.insight-card {
  padding: 1rem;
  background: lighten($body-bg, 5%);
  border-radius: 0.5rem;
  border: 1px solid rgba($body-color, 0.2);
  margin-bottom: 1rem;
  &.full-width {
    margin-top: 0.5rem;
  }
  h4 {
    color: $primary;
    margin-bottom: 0.5rem;
  }
}

.download-pdf-section {
  text-align: center;
  padding: 2rem;
  background: lighten($body-bg, 3%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;

  .download-pdf-btn {
    margin-top: 1rem;
    padding: 0.75rem 2rem;
    font-size: 1.1rem;
    font-weight: 600;

    i {
      margin-right: 0.5rem;
    }
  }
}

.positive {
  color: $secondary;
}

.forecast-table-container {
  margin-top: 1.5rem;
  overflow-x: auto;
}

.scenario-table-container {
  margin-top: 1rem;
  overflow-x: auto;
  background: lighten($body-bg, 3%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1rem;
}

.scenario-table,
.forecast-table {
  width: 100%;
  border-collapse: collapse;
  background: lighten($body-bg, 3%);
  border-radius: 0.375rem;
  overflow: hidden;

  thead th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-weight: 700;
    color: $primary;
    border-bottom: 2px solid rgba($primary, 0.3);
    font-size: 0.9rem;
  }

  tbody tr {
    border-bottom: 1px solid rgba($body-color, 0.1);

    &:last-child {
      border-bottom: none;
    }
  }

  tbody td {
    padding: 0.75rem 1rem;
    color: $body-color;
    font-size: 0.95rem;

    &:not(:first-child) {
      text-align: right;
    }

    &.positive {
      color: $secondary;
      font-weight: 700;
    }
  }

  .total-row td {
    font-weight: 700;
  }
}

@media (max-width: 1199px) {
  .input-section {
    padding: 1.5rem;
  }
}

@media (max-width: 992px) {
  .input-section {
    padding: 1.25rem;
  }
}

@media (max-width: 991px) {
  .inputs-panel,
  .metrics-panel {
    height: auto;
    max-height: none;
  }

  .metrics-panel {
    border-left: none;
    border-top: 1px solid rgba($body-color, 0.1);
  }

  .inputs-panel,
  .metrics-panel {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .calculator-main {
    min-height: 400px;
  }

  .calculator-header {
    padding: 1.5rem 1rem 1rem 1rem;

    .calculator-title {
      font-size: 1.5rem;
    }
  }

  .main-tabs {
    ::v-deep .nav-tabs {
      padding: 0 1rem;
      .nav-item .nav-link {
        padding: 0.75rem 1rem;
        font-size: 1rem;
      }
    }
  }

  .inputs-panel,
  .metrics-panel {
    padding: 1rem;
  }

  .input-section {
    padding: 1rem;
    margin-bottom: 1rem;

    .section-title {
      font-size: 1rem;
    }
  }

  .form-field {
    margin-bottom: 1rem;

    .input-field {
      max-width: 100%;
    }
  }

  .analysis-container {
    padding: 1rem;
  }

  .analysis-section {
    margin-bottom: 2rem;

    .section-header {
      font-size: 1.25rem;
    }
  }

  .executive-summary {
    padding: 1.5rem;

    .summary-metric {
      margin-bottom: 1rem;
    }
  }
}
</style>
